import React, { useState, useEffect, useCallback } from 'react';
import { Printer, Loader2, Bluetooth, WifiOff, AlertCircle, HelpCircle, ChevronDown, ChevronUp, RotateCcw } from 'lucide-react';
import { Button } from '../ui/Button';
import { usePhomemoSettings } from '../../hooks/usePhomemoSettings';
import {
  isPhomemoAvailable,
  isSecureContext,
  connect,
  reconnect,
  tryReconnectFromCache,
  disconnect,
  getConnectionState,
  onConnectionChange,
} from '../../printers/phomemo/bleClient';
import { testPrint } from '../../printers/phomemo/phomemoPrint';
import type { PhomemoConnectionState, LabelSpec } from '../../printers/phomemo/types';
import { DEFAULT_LABEL_SPEC, mergeLabelSpec, clampLabelSpecValue } from '../../printers/phomemo/types';

const LABEL_PRESETS: { id: string; label: string; widthIn: number; heightIn: number; orientation: 'portrait' | 'landscape' }[] = [
  { id: '4x6', label: '4×6 in (shipping)', widthIn: 4, heightIn: 6, orientation: 'portrait' },
  { id: '2x1', label: '2×1 in (shelf)', widthIn: 2, heightIn: 1, orientation: 'landscape' },
  { id: '2x1.25', label: '2×1.25 in (shelf)', widthIn: 2, heightIn: 1.25, orientation: 'landscape' },
  { id: 'custom', label: 'Custom', widthIn: 0, heightIn: 0, orientation: 'portrait' },
];

export const PhomemoPrinterSettings: React.FC = () => {
  const { settings, loading: settingsLoading, saveSettings, effectiveLabelSpec } = usePhomemoSettings();
  const [labelSpecOpen, setLabelSpecOpen] = useState(true);
  const [connectionState, setConnectionState] = useState<PhomemoConnectionState>('disconnected');
  const [deviceName, setDeviceName] = useState<string | undefined>();
  const [connecting, setConnecting] = useState(false);
  const [reconnecting, setReconnecting] = useState(false);
  const [testPrinting, setTestPrinting] = useState(false);
  const [printProgress, setPrintProgress] = useState<{ chunk: number; total: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const testPrintAbortRef = React.useRef<AbortController | null>(null);
  const attemptedAutoReconnectRef = React.useRef(false);

  const available = isPhomemoAvailable() && isSecureContext();

  const refreshState = useCallback(async () => {
    const { state, deviceName: name } = await getConnectionState();
    setConnectionState(state);
    setDeviceName(name);
  }, []);

  useEffect(() => {
    refreshState();
  }, [refreshState]);

  useEffect(() => {
    const unsubscribe = onConnectionChange((state, name) => {
      setConnectionState(state);
      setDeviceName(name);
    });
    return unsubscribe;
  }, []);

  useEffect(() => {
    if (settingsLoading || !settings.deviceId || attemptedAutoReconnectRef.current) return;
    let cancelled = false;
    attemptedAutoReconnectRef.current = true;
    (async () => {
      const { state } = await getConnectionState();
      if (cancelled || state === 'connected') return;
      const ok = await tryReconnectFromCache(settings.deviceId!, {
        autoSelectWritableCharacteristic: settings.autoSelectWritableCharacteristic !== false,
      });
      if (ok && !cancelled) refreshState();
    })();
    return () => { cancelled = true; };
  }, [settingsLoading, settings.deviceId, refreshState]);

  useEffect(() => {
    const handler = () => {
      if (document.visibilityState === 'visible') refreshState();
    };
    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  }, [refreshState]);

  const connectOptions = {
    autoSelectWritableCharacteristic: settings.autoSelectWritableCharacteristic !== false,
    printerMode: settings.printerMode ?? 'phomemo',
  };

  const handleConnect = async () => {
    setError(null);
    setConnecting(true);
    try {
      const { deviceId, deviceName: name } = await connect(connectOptions);
      setConnectionState('connected');
      setDeviceName(name);
      await saveSettings({ ...settings, deviceId, deviceName: name });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Connection failed');
      setConnectionState('disconnected');
    } finally {
      setConnecting(false);
    }
  };

  const handleReconnect = async () => {
    setError(null);
    setReconnecting(true);
    try {
      const id = settings.deviceId;
      const { deviceId, deviceName: name } = id
        ? await reconnect(id, connectOptions)
        : await connect(connectOptions);
      setConnectionState('connected');
      setDeviceName(name);
      await saveSettings({ ...settings, deviceId, deviceName: name });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Reconnect failed');
      setConnectionState('disconnected');
    } finally {
      setReconnecting(false);
    }
  };

  const handleDisconnect = async () => {
    setError(null);
    try {
      await disconnect();
      setConnectionState('disconnected');
      setDeviceName(undefined);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Disconnect failed');
    }
  };

  const handleTestPrint = async () => {
    setError(null);
    setPrintProgress(null);
    setTestPrinting(true);
    const ac = new AbortController();
    testPrintAbortRef.current = ac;
    try {
      const { state } = await getConnectionState();
      if (state !== 'connected') {
        setError('Printer disconnected. Please reconnect and try again.');
        setConnectionState('disconnected');
        return;
      }
      await testPrint({
        density: settings.density,
        protocol: printerProtocol,
        onProgress: (chunk, total) => setPrintProgress({ chunk, total }),
        signal: ac.signal,
      });
    } catch (e) {
      if ((e as Error)?.message !== 'Cancelled') {
        setError(e instanceof Error ? e.message : 'Test print failed');
      }
    } finally {
      setTestPrinting(false);
      setPrintProgress(null);
      testPrintAbortRef.current = null;
    }
  };

  const handleCancelTestPrint = () => {
    testPrintAbortRef.current?.abort();
  };

  const density = settings.density ?? 50;
  const barcodeSafeMode = settings.barcodeSafeMode ?? false;
  const printerProtocol = settings.printerProtocol ?? 'escpos';
  const autoSelectWritable = settings.autoSelectWritableCharacteristic !== false;
  const printerMode = settings.printerMode ?? 'phomemo';

  const updateLabelSpec = useCallback(
    (patch: Partial<LabelSpec>) => {
      const next = { ...(settings.labelSpec || {}), ...patch } as Partial<LabelSpec>;
      saveSettings({ ...settings, labelSpec: next });
    },
    [settings, saveSettings]
  );

  const presetMatch = LABEL_PRESETS.find(
    (p) =>
      p.id !== 'custom' &&
      p.widthIn === effectiveLabelSpec.widthIn &&
      p.heightIn === effectiveLabelSpec.heightIn &&
      p.orientation === effectiveLabelSpec.orientation
  );
  const sizePreset = presetMatch?.id ?? 'custom';

  const handlePresetChange = (id: string) => {
    const p = LABEL_PRESETS.find((x) => x.id === id);
    if (p && p.id !== 'custom') updateLabelSpec({ widthIn: p.widthIn, heightIn: p.heightIn, orientation: p.orientation });
  };

  const handleResetLabelSpec = () => {
    saveSettings({ ...settings, labelSpec: {} });
  };

  if (settingsLoading) {
    return (
      <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
        <Loader2 className="h-5 w-5 animate-spin" />
        <span>Loading printer settings…</span>
      </div>
    );
  }

  if (!available) {
    return (
      <div className="space-y-4">
        <div className="rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-4">
          <div className="flex items-start gap-3">
            <WifiOff className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-medium text-amber-800 dark:text-amber-300">Bluetooth thermal printer</h4>
              <p className="text-sm text-amber-700 dark:text-amber-400 mt-1">
                Works on <strong>Chrome or Edge on Android</strong> only. Use HTTPS. Bluetooth required.
              </p>
            </div>
          </div>
        </div>
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 p-4">
          <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2 flex items-center gap-2">
            <AlertCircle className="h-4 w-4" />
            Troubleshooting
          </h4>
          <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1 list-disc list-inside">
            <li>Open this app in <strong>Chrome</strong> or <strong>Edge</strong> on an Android device.</li>
            <li>Ensure the page is served over <strong>HTTPS</strong> (or localhost).</li>
            <li>Grant <strong>Bluetooth</strong> permission when the browser asks.</li>
            <li>Turn the printer on and ensure it is in range before connecting.</li>
          </ul>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center gap-2 text-gray-900 dark:text-gray-100">
          <Bluetooth className="h-5 w-5" />
          <h3 className="text-lg font-medium">Your printer</h3>
        </div>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Settings are saved per user. Bluetooth thermal printer.
        </p>
      </div>

      <div className="rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-4">
        <p className="text-sm text-amber-800 dark:text-amber-300 font-medium mb-2">
          ⚠️ Important: Do NOT pair in Android Bluetooth settings
        </p>
        <ul className="text-sm text-amber-700 dark:text-amber-400 space-y-1 list-disc list-inside">
          <li>Turn printer <strong>ON</strong></li>
          <li>Ensure <strong>Bluetooth</strong> is enabled on this device</li>
          <li><strong>Do not pair</strong> the printer in Android Bluetooth settings (causes connection failures)</li>
          <li>Tap <strong>Connect</strong> below and select printer in the <strong>browser dialog</strong></li>
          <li>Works on <strong>Chrome/Edge on Android</strong> (or supported desktop)</li>
        </ul>
      </div>

      <div className="flex items-center gap-2">
        <span className="text-sm text-gray-600 dark:text-gray-400">Status:</span>
        {connectionState === 'connecting' && (
          <>
            <Loader2 className="h-4 w-4 animate-spin text-gray-500" />
            <span>Connecting…</span>
          </>
        )}
        {connectionState === 'connected' && (
          <span className="text-green-600 dark:text-green-400">
            Connected to {deviceName || 'printer'}
          </span>
        )}
        {connectionState === 'disconnected' && (
          <span className="text-gray-500 dark:text-gray-400">Disconnected</span>
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        {connectionState !== 'connected' ? (
          <>
            <Button
              variant="primary"
              onClick={handleConnect}
              disabled={connecting || reconnecting}
            >
              {connecting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Connecting…
                </>
              ) : (
                <>
                  <Bluetooth className="h-4 w-4 mr-2" />
                  Connect (choose printer)
                </>
              )}
            </Button>
            {settings.deviceId && (
              <Button
                variant="secondary"
                onClick={handleReconnect}
                disabled={connecting || reconnecting}
              >
                {reconnecting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Reconnecting…
                  </>
                ) : (
                  'Reconnect'
                )}
              </Button>
            )}
          </>
        ) : (
          <Button variant="secondary" onClick={handleDisconnect}>
            Disconnect
          </Button>
        )}
      </div>

      {settings.deviceId && (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Remembered: {settings.deviceName || settings.deviceId}
        </p>
      )}

      <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/30 p-4 space-y-4">
        <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Printer type</h4>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Connection mode</label>
          <select
            value={printerMode}
            onChange={(e) => saveSettings({ ...settings, printerMode: e.target.value as 'phomemo' | 'generic' | 'any' })}
            className="rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-3 py-1.5 text-sm"
          >
            <option value="phomemo">Phomemo (service FF00) — D520BT, etc.</option>
            <option value="generic">Generic thermal (FFE0/NUS)</option>
            <option value="any">Any device (fallback)</option>
          </select>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            <strong>Phomemo mode</strong> filters by service UUID (FF00) for reliable connection regardless of device name. 
            Use <strong>Generic</strong> for non-Phomemo thermal printers, or <strong>Any device</strong> for unknown printers.
          </p>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Printer language</label>
          <select
            value={printerProtocol}
            onChange={(e) => saveSettings({ ...settings, printerProtocol: e.target.value as 'escpos' | 'tspl_zpl' })}
            className="rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-3 py-1.5 text-sm"
          >
            <option value="escpos">ESC/POS raster (bitmap)</option>
            <option value="tspl_zpl">TSPL / ZPL</option>
          </select>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Some printers speak ESC/POS raster; others use TSPL or ZPL. Choose the one that matches your printer.
          </p>
        </div>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={autoSelectWritable}
            onChange={(e) => saveSettings({ ...settings, autoSelectWritableCharacteristic: e.target.checked })}
            className="rounded border-gray-300 dark:border-gray-600"
          />
          <span className="text-sm text-gray-700 dark:text-gray-300">Auto-select writable characteristic</span>
        </label>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          When enabled (recommended), the app scans all BLE services for a writable characteristic if known pairs fail. Turn off only if your printer uses a known service (e.g. 0xFFE0/0xFFE1 or Nordic UART).
        </p>
      </div>

      <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/30 p-4 space-y-4">
        <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Print quality</h4>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Print density</label>
          <input
            type="range"
            min={0}
            max={100}
            value={density}
            onChange={(e) => saveSettings({ ...settings, density: parseInt(e.target.value, 10) })}
            className="w-full max-w-xs"
          />
          <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">{density}</span>
        </div>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={barcodeSafeMode}
            onChange={(e) => saveSettings({ ...settings, barcodeSafeMode: e.target.checked })}
            className="rounded border-gray-300 dark:border-gray-600"
          />
          <span className="text-sm text-gray-700 dark:text-gray-300">Barcode Safe Mode (crisp barcodes)</span>
        </label>
      </div>

      <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        <button
          type="button"
          onClick={() => setLabelSpecOpen((o) => !o)}
          className="w-full flex items-center justify-between px-4 py-3 text-left bg-gray-50 dark:bg-gray-900/50 hover:bg-gray-100 dark:hover:bg-gray-800/50 transition-colors"
        >
          <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">Label specifications</span>
          {labelSpecOpen ? <ChevronUp className="h-4 w-4 text-gray-500" /> : <ChevronDown className="h-4 w-4 text-gray-500" />}
        </button>
        {labelSpecOpen && (
          <div className="p-4 space-y-4 border-t border-gray-200 dark:border-gray-700">
            <div className="flex flex-wrap items-center gap-2">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Size</label>
              <select
                value={sizePreset}
                onChange={(e) => handlePresetChange(e.target.value)}
                className="rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-3 py-1.5 text-sm"
              >
                {LABEL_PRESETS.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.label}
                  </option>
                ))}
              </select>
              {sizePreset === 'custom' && (
                <span className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                  <input
                    type="number"
                    min={0.5}
                    max={12}
                    step={0.25}
                    value={effectiveLabelSpec.widthIn}
                    onChange={(e) => updateLabelSpec({ widthIn: clampLabelSpecValue('widthIn', parseFloat(e.target.value) || 0.5) })}
                    className="w-16 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-2 py-1 text-sm"
                  />
                  ×
                  <input
                    type="number"
                    min={0.5}
                    max={18}
                    step={0.25}
                    value={effectiveLabelSpec.heightIn}
                    onChange={(e) => updateLabelSpec({ heightIn: clampLabelSpecValue('heightIn', parseFloat(e.target.value) || 0.5) })}
                    className="w-16 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-2 py-1 text-sm"
                  />
                  in
                </span>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Orientation</label>
                <select
                  value={effectiveLabelSpec.orientation}
                  onChange={(e) => updateLabelSpec({ orientation: e.target.value as 'portrait' | 'landscape' })}
                  className="rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-3 py-1.5 text-sm"
                >
                  <option value="portrait">Portrait</option>
                  <option value="landscape">Landscape</option>
                </select>
              </div>
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Margin (pt)</label>
                <input
                  type="number"
                  min={0}
                  max={72}
                  value={effectiveLabelSpec.marginPt}
                  onChange={(e) => updateLabelSpec({ marginPt: clampLabelSpecValue('marginPt', parseInt(e.target.value, 10) || 0) })}
                  title="Label margin in points (0–72). Affects PDF layout."
                  className="w-20 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-2 py-1 text-sm"
                />
                <span title="Label margin in points (0–72). Affects PDF layout."><HelpCircle className="h-4 w-4 text-gray-400" /></span>
              </div>
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">DPI</label>
                <select
                  value={effectiveLabelSpec.dpi}
                  onChange={(e) => updateLabelSpec({ dpi: clampLabelSpecValue('dpi', parseInt(e.target.value, 10)) })}
                  title="Output resolution. 203 for typical thermal; 300 for higher quality."
                  className="rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-3 py-1.5 text-sm"
                >
                  <option value={203}>203</option>
                  <option value={300}>300</option>
                </select>
                <span title="Output resolution. 203 for typical thermal; 300 for higher quality."><HelpCircle className="h-4 w-4 text-gray-400" /></span>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Title font (pt)</label>
                <input
                  type="number"
                  min={6}
                  max={24}
                  value={effectiveLabelSpec.titleFontPt}
                  onChange={(e) => updateLabelSpec({ titleFontPt: clampLabelSpecValue('titleFontPt', parseInt(e.target.value, 10) || 6) })}
                  className="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-2 py-1.5 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">SKU font (pt)</label>
                <input
                  type="number"
                  min={6}
                  max={20}
                  value={effectiveLabelSpec.skuFontPt}
                  onChange={(e) => updateLabelSpec({ skuFontPt: clampLabelSpecValue('skuFontPt', parseInt(e.target.value, 10) || 6) })}
                  className="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-2 py-1.5 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Location font (pt)</label>
                <input
                  type="number"
                  min={6}
                  max={20}
                  value={effectiveLabelSpec.locationFontPt}
                  onChange={(e) => updateLabelSpec({ locationFontPt: clampLabelSpecValue('locationFontPt', parseInt(e.target.value, 10) || 6) })}
                  className="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-2 py-1.5 text-sm"
                />
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Barcode max width (pt)</label>
                <input
                  type="number"
                  min={50}
                  max={400}
                  value={effectiveLabelSpec.barcodeMaxWidthPt}
                  onChange={(e) => updateLabelSpec({ barcodeMaxWidthPt: clampLabelSpecValue('barcodeMaxWidthPt', parseInt(e.target.value, 10) || 50) })}
                  className="w-24 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-2 py-1 text-sm"
                />
              </div>
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Barcode height (pt)</label>
                <input
                  type="number"
                  min={20}
                  max={120}
                  value={effectiveLabelSpec.barcodeHeightPt}
                  onChange={(e) => updateLabelSpec({ barcodeHeightPt: clampLabelSpecValue('barcodeHeightPt', parseInt(e.target.value, 10) || 20) })}
                  className="w-24 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-2 py-1 text-sm"
                />
              </div>
            </div>
            <div className="flex items-center gap-2 pt-2">
              <Button variant="secondary" onClick={handleResetLabelSpec} className="flex items-center gap-2">
                <RotateCcw className="h-4 w-4" />
                Reset to defaults
              </Button>
            </div>
          </div>
        )}
      </div>

      <div className="space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="primary"
            onClick={handleTestPrint}
            disabled={connectionState !== 'connected' || testPrinting}
          >
            {testPrinting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Printing…
              </>
            ) : (
              <>
                <Printer className="h-4 w-4 mr-2" />
                Test Print
              </>
            )}
          </Button>
          {testPrinting && (
            <Button variant="secondary" onClick={handleCancelTestPrint}>
              Cancel
            </Button>
          )}
        </div>
        {testPrinting && printProgress && (
          <div className="text-sm text-gray-600 dark:text-gray-400">
            Sending chunk {printProgress.chunk} of {printProgress.total}
            <div className="mt-1 h-1.5 w-full max-w-xs bg-gray-200 dark:bg-gray-700 rounded overflow-hidden">
              <div
                className="h-full bg-primary-500 rounded"
                style={{ width: `${(100 * printProgress.chunk) / printProgress.total}%` }}
              />
            </div>
          </div>
        )}
      </div>

      <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 p-4">
        <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2 flex items-center gap-2">
          <AlertCircle className="h-4 w-4" />
          Troubleshooting
        </h4>
        <div className="text-sm text-gray-600 dark:text-gray-400 space-y-3">
          <p className="font-medium text-gray-700 dark:text-gray-300">Common connection issues:</p>
          <ul className="space-y-1 list-disc list-inside">
            <li>Grant <strong>Bluetooth</strong> permission when prompted</li>
            <li>Ensure printer is <strong>on</strong> and in range</li>
            <li>If connection fails, try <strong>Reconnect</strong> or <strong>Connect</strong> again</li>
          </ul>
          <p className="font-medium text-gray-700 dark:text-gray-300">If you see "service FF00 not visible" or "characteristic FF02 failed":</p>
          <ol className="space-y-1 list-decimal list-inside pl-2">
            <li>Close <strong>Labelife</strong> or any other printer apps</li>
            <li>Ensure printer is <strong>NOT connected</strong> to another phone/device</li>
            <li><strong>Android Bluetooth settings → Forget/unpair</strong> this printer</li>
            <li>Turn <strong>Bluetooth off/on</strong></li>
            <li><strong>Power-cycle</strong> the printer (off/on)</li>
            <li>Retry connection from this app <strong>only</strong> (do not OS-pair manually)</li>
          </ol>
          <p className="text-xs italic">This fixes ~95% of "no writable characteristic" errors on Phomemo printers.</p>
        </div>
      </div>

      {error && (
        <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      )}
    </div>
  );
};
